import CloseIcon from '@mui/icons-material/Close'
import {
    Box,
    Chip,
    CircularProgress,
    Dialog,
    IconButton,
    InputBase,
    List,
    ListItemButton,
    Paper,
    Typography,
    useTheme
} from '@mui/material'
import PropTypes from 'prop-types'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import useAuth from '@/hooks/useAuth'
import { useFullScreenDialog } from '@/hooks/useFullScreenDialog'
import SearchIcon from '@/icons/Search'
import { splitCategoryLabel } from '../docsCategory.js'
import { searchDocs } from './docsSearch'
import { loadDocsSearchIndex } from './docsSearchIndex'

/**
 * Command-palette search over the docs.
 *
 * The index is fetched on first open, not on mount — a reader who never
 * searches pays nothing. Which shards arrive is decided by the signed-in
 * user (read here via `useAuth()`), so results can only ever contain pages
 * this reader could open. See `docsSearchIndex.js`.
 */

/** Renders a snippet with its matched ranges emphasised. */
function Highlighted({ text, ranges }) {
    const theme = useTheme()
    if (!ranges.length) return <>{text}</>

    const parts = []
    let cursor = 0
    for (const [start, end] of ranges) {
        if (start > cursor) parts.push(text.slice(cursor, start))
        parts.push(
            <Box
                component="mark"
                key={start}
                sx={{
                    px: 0.25,
                    borderRadius: 0.5,
                    bgcolor: 'transparent',
                    color: theme.palette.primary.main,
                    fontWeight: 600
                }}
            >
                {text.slice(start, end)}
            </Box>
        )
        cursor = end
    }
    if (cursor < text.length) parts.push(text.slice(cursor))
    return <>{parts}</>
}

Highlighted.propTypes = {
    text: PropTypes.string.isRequired,
    ranges: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.number)).isRequired
}

export default function DocsSearchDialog({ open, onClose }) {
    const fullScreen = useFullScreenDialog()
    const navigate = useNavigate()
    const { user } = useAuth()
    const [query, setQuery] = useState('')
    const [entries, setEntries] = useState(null)
    const [loading, setLoading] = useState(false)
    const [active, setActive] = useState(0)
    const listRef = useRef(null)

    // Fetch the shards the first time the dialog opens, then keep them.
    //
    // The "have we started" flag is a ref, not the `loading` state. As
    // state it has to be a dependency, and setting it inside the effect
    // then re-runs the effect, whose cleanup flips `cancelled` and
    // discards the load that had only just been kicked off — the dialog
    // spins forever with an empty list.
    const startedRef = useRef(false)
    useEffect(() => {
        if (!open || startedRef.current) return
        startedRef.current = true
        setLoading(true)

        let cancelled = false
        loadDocsSearchIndex(user)
            .then((loaded) => {
                if (!cancelled) setEntries(loaded)
            })
            .finally(() => {
                if (!cancelled) setLoading(false)
            })
        return () => {
            cancelled = true
        }
    }, [open, user])

    const results = useMemo(
        () => (entries ? searchDocs(entries, query) : []),
        [entries, query]
    )

    // Any new query puts the selection back at the top; without this the
    // highlight can sit past the end of a shorter result list.
    useEffect(() => {
        setActive(0)
    }, [query])

    const go = useCallback(
        (result) => {
            onClose()
            navigate(
                result.anchor ? `${result.path}#${result.anchor}` : result.path
            )
        },
        [navigate, onClose]
    )

    const onKeyDown = (event) => {
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
            event.preventDefault()
            if (!results.length) return
            const delta = event.key === 'ArrowDown' ? 1 : -1
            const next = (active + delta + results.length) % results.length
            setActive(next)
            listRef.current
                ?.querySelectorAll('li')
                [next]?.scrollIntoView({ block: 'nearest' })
        } else if (event.key === 'Enter') {
            event.preventDefault()
            const result = results[active]
            if (result) go(result)
        }
    }

    // Reset between openings so the next Cmd+K starts clean rather than
    // showing the previous search's results.
    useEffect(() => {
        if (!open) setQuery('')
    }, [open])

    return (
        <Dialog
            fullScreen={fullScreen}
            open={open}
            onClose={onClose}
            fullWidth
            maxWidth="sm"
            slotProps={{
                paper: {
                    // Command-palette position on desktop; a phone gets the
                    // whole screen, since the keyboard takes the bottom half.
                    sx: fullScreen
                        ? undefined
                        : { position: 'fixed', top: 64, m: 0, borderRadius: 2 }
                }
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                    px: 2,
                    py: 1.5,
                    borderBottom: 1,
                    borderColor: 'divider'
                }}
            >
                <SearchIcon fontSize="small" />
                <InputBase
                    autoFocus
                    fullWidth
                    placeholder="Search the documentation"
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    onKeyDown={onKeyDown}
                    inputProps={{ 'aria-label': 'Search the documentation' }}
                />
                {loading && <CircularProgress size={18} />}
                {fullScreen ? (
                    <IconButton
                        aria-label="Close search"
                        onClick={onClose}
                        edge="end"
                    >
                        <CloseIcon />
                    </IconButton>
                ) : (
                    <Chip label="Esc" size="small" variant="outlined" />
                )}
            </Box>

            <Box
                sx={
                    fullScreen
                        ? { flex: '1 1 auto', overflowY: 'auto' }
                        : { maxHeight: '60vh', overflowY: 'auto' }
                }
            >
                {query && !loading && !results.length && (
                    <Typography
                        color="text.secondary"
                        sx={{ px: 2, py: 4, textAlign: 'center' }}
                    >
                        No matches for &ldquo;{query}&rdquo;.
                    </Typography>
                )}

                {!query && (
                    <Typography
                        color="text.secondary"
                        variant="body2"
                        sx={{ px: 2, py: 4, textAlign: 'center' }}
                    >
                        Search titles, headings and page text. Results cover
                        only the documentation your account can open.
                    </Typography>
                )}

                <List ref={listRef} dense disablePadding>
                    {results.map((result, index) => (
                        <ListItemButton
                            key={`${result.path}#${result.anchor ?? ''}`}
                            selected={index === active}
                            onMouseEnter={() => setActive(index)}
                            onClick={() => go(result)}
                            sx={{
                                display: 'block',
                                px: 2,
                                py: 1.25,
                                borderBottom: 1,
                                borderColor: 'divider'
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'baseline',
                                    gap: 1,
                                    flexWrap: 'wrap'
                                }}
                            >
                                <Typography variant="subtitle2">
                                    {result.title}
                                </Typography>
                                {result.heading &&
                                    result.heading !== result.title && (
                                        <Typography
                                            variant="body2"
                                            color="text.secondary"
                                        >
                                            {result.heading}
                                        </Typography>
                                    )}
                                {result.category && (
                                    <Chip
                                        label={
                                            splitCategoryLabel(result.category)
                                                .text
                                        }
                                        size="small"
                                        variant="outlined"
                                        sx={{
                                            height: 18,
                                            fontSize: '0.6875rem'
                                        }}
                                    />
                                )}
                            </Box>
                            <Typography
                                variant="body2"
                                color="text.secondary"
                                sx={{ mt: 0.5 }}
                            >
                                <Highlighted
                                    text={result.snippet.text}
                                    ranges={result.snippet.ranges}
                                />
                            </Typography>
                        </ListItemButton>
                    ))}
                </List>
            </Box>

            {Boolean(results.length) && (
                <Paper
                    elevation={0}
                    sx={{
                        px: 2,
                        py: 1,
                        borderTop: 1,
                        borderColor: 'divider',
                        display: 'flex',
                        gap: 2
                    }}
                >
                    <Typography variant="caption" color="text.secondary">
                        ↑↓ to navigate
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        ↵ to open
                    </Typography>
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ ml: 'auto' }}
                    >
                        {results.length} result
                        {results.length === 1 ? '' : 's'}
                    </Typography>
                </Paper>
            )}
        </Dialog>
    )
}

DocsSearchDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired
}
